import type { ChargeType } from './billing';
import type { CurrencyCode, IsoDate, IsoDateTime } from './common';
import type { IntegratedChannel, MarketplaceChannel } from './marketplace';
import type { OrderStatus } from './orders';
import type { ShippingModel } from './shipping';

// Kârlılık: what the seller really earns on its orders. The ciro, less every marketplace deduction (its
// finance records, or an estimate before they exist), less the warehouse's own fees and the cost of the
// goods the seller may enter. Profit is worked out net of KDV (a VAT-registered seller passes it on); the
// cash the marketplace pays out is shown beside it. Seller-only: no warehouse user reads a margin.

// --- marketplace money movements ----------------------------------------------------------------------

export const FINANCE_KINDS = [
  'sale',
  'sale_reversal',
  'discount_seller',
  'discount_marketplace',
  'commission',
  'commission_refund',
  'shipping',
  'return_shipping',
  'service_fee',
  'payment_fee',
  'penalty',
  'advertising',
  'adjustment',
  'withholding',
  'payout',
  'excluded',
  'other',
] as const;
/** What a marketplace money movement is, whatever the marketplace calls it (its own label is kept beside). */
export type FinanceKind = (typeof FINANCE_KINDS)[number];

export const FINANCE_KIND_LABELS: Readonly<Record<FinanceKind, string>> = {
  sale: 'Satış',
  sale_reversal: 'İade (satış geri alındı)',
  discount_seller: 'Satıcı indirimi',
  discount_marketplace: 'Pazaryerinin karşıladığı indirim',
  commission: 'Komisyon',
  commission_refund: 'Komisyon iadesi',
  shipping: 'Kargo',
  return_shipping: 'İade kargosu',
  service_fee: 'Platform / hizmet bedeli',
  payment_fee: 'Ödeme / işlem bedeli',
  penalty: 'Ceza',
  advertising: 'Reklam',
  adjustment: 'Düzeltme',
  withholding: 'Stopaj',
  payout: 'Ödeme ve aktarım',
  excluded: 'Kâra katılmayan belge',
  other: 'Tanınmayan kayıt',
};

/**
 * Where a kind goes on the page: the ciro, a group of marketplace deductions, the withheld tax, or outside
 * the profit (payouts and transfers, documents that repeat what other rows hold). A discount the marketplace
 * pays the seller for (Hepsiburada's CampaignDiscount: the buyer paid less, Hepsiburada pays the rest) is part
 * of what the goods sold for; Trendyol's own promotions, debited and invoiced back by the seller, are documents.
 */
export type FinanceGroup = 'ciro' | 'commission' | 'cargo' | 'fees' | 'returns' | 'other' | 'withholding' | 'outside';

export const FINANCE_KIND_GROUP: Readonly<Record<FinanceKind, FinanceGroup>> = {
  sale: 'ciro',
  sale_reversal: 'returns',
  discount_seller: 'returns',
  discount_marketplace: 'ciro',
  commission: 'commission',
  commission_refund: 'commission',
  shipping: 'cargo',
  return_shipping: 'cargo',
  service_fee: 'fees',
  payment_fee: 'fees',
  penalty: 'other',
  advertising: 'other',
  adjustment: 'other',
  withholding: 'withholding',
  payout: 'outside',
  excluded: 'outside',
  other: 'other',
};

/**
 * The KDV in a row the marketplace did not split: a sale, its return and the seller's discount carry the
 * product's rate (the order line's); the marketplace's charges 20 %; payouts and stopaj none.
 */
export const FINANCE_KIND_VAT: Readonly<Record<FinanceKind, 'line' | 'standard' | 'none'>> = {
  sale: 'line',
  sale_reversal: 'line',
  discount_seller: 'line',
  discount_marketplace: 'line',
  commission: 'standard',
  commission_refund: 'standard',
  shipping: 'standard',
  return_shipping: 'standard',
  service_fee: 'standard',
  payment_fee: 'standard',
  penalty: 'standard',
  advertising: 'standard',
  adjustment: 'standard',
  withholding: 'none',
  payout: 'none',
  excluded: 'none',
  other: 'standard',
};

/** KDV % where neither the row nor an order line says: the marketplaces' services, and an unknown product rate. */
export const STANDARD_VAT_RATE = 20;

/**
 * One marketplace money movement, as every adapter gives it: signed from the seller's view (+ income,
 * − cost). Only the marketplace's codes, numbers, amounts and dates: never a buyer's name or address, nor
 * a free text that could carry one.
 */
export interface NormalizedFinanceRow {
  /** where it was read: 'trendyol.settlements', 'trendyol.otherfinancials', 'trendyol.cargo_items', 'hepsiburada.transactions' */
  readonly source: string;
  /** unique in its source and account; a Trendyol settlement row is two rows (<id>:sale, <id>:commission) */
  readonly externalId: string;
  readonly kind: FinanceKind;
  /** the marketplace's own name of the movement ("Satış", "Kargo Fatura", "ShipmentCostSharingExpense") */
  readonly label: string;
  /** decimal string, signed */
  readonly amount: string;
  /** the KDV in it, signed like the amount; null: not said (the rules of FINANCE_KIND_VAT apply) */
  readonly vatAmount: string | null;
  /** the amount includes KDV (false: net; the KDV, when said, comes on top) */
  readonly vatIncluded: boolean;
  readonly currency: CurrencyCode;
  readonly occurredAt: IsoDateTime;
  readonly externalOrderId: string | null;
  readonly externalPackageId: string | null;
  /** the parcel's cargo tracking number (Trendyol's cargo invoice items: parcelUniqueId) */
  readonly trackingNumber?: string | null;
  readonly barcode: string | null;
  /** the marketplace's product key (Hepsiburada SKU) */
  readonly channelSku: string | null;
  readonly quantity: number | null;
  /** the marketplace's row, its allowed fields only (ids, types, amounts, dates) */
  readonly raw: Readonly<Record<string, unknown>>;
}

/** How far the nightly finance sync reads (finance.sync_account). */
export const FINANCE_SYNC = {
  /** an account's first sync reads this far back */
  backfillDays: 180,
  /** every later sync reads again from this long before where the last one ended (rows change: payment dates, late invoices) */
  overlapDays: 30,
  /** rows of orders imported after them are linked again this far back */
  relinkDays: 180,
  /** "Şimdi güncelle" at most this often per account */
  manualEveryMinutes: 15,
  /** one sync of an account at a time; one that has not ended after this long is taken as stopped (the worker died) */
  runningMinutes: 60,
} as const;

/** Why a marketplace keeps its finance records from the warehouse: the key lacks the role. Orders are not affected. */
export function financeAuthText(channel: IntegratedChannel): string {
  if (channel === 'trendyol') {
    return 'Trendyol finans kayıtlarını vermedi: API anahtarınıza “Muhasebe ve Finans Entegrasyonu” yetkisini ekleyin (Trendyol Satıcı Paneli → Hesap Bilgilerim → Entegrasyon Bilgileri → Pronto Depo). Siparişleriniz bundan etkilenmez.';
  }
  if (channel === 'hepsiburada') {
    return 'Hepsiburada muhasebe servisi anahtarınızı kabul etmedi: entegratörünüzün Hepsiburada finans (muhasebe) servisine yetkisini Hepsiburada’dan isteyin. Siparişleriniz bundan etkilenmez.';
  }
  return 'Pazaryeri finans kayıtlarını vermedi: API anahtarının yetkilerini kontrol edin. Siparişleriniz bundan etkilenmez.';
}

// --- the seller's product cost -------------------------------------------------------------------------

/** numeric(14,4): ten whole digits and four decimals at most */
export const UNIT_COST_PATTERN = /^\d{1,10}(\.\d{1,4})?$/;

/** A product's unit cost as the seller set it (KDV hariç), and when it last changed. */
export interface ProductCostView {
  readonly productId: string;
  readonly sku: string;
  readonly name: string;
  readonly imageUrl: string | null;
  /** in force now; null: none entered, or cleared */
  readonly unitCost: string | null;
  readonly currency: CurrencyCode;
  /** the last change (a cleared cost too); null: never set */
  readonly changedAt: IsoDateTime | null;
}

// --- the report -------------------------------------------------------------------------------------------

export const PROFIT_STATES = ['final', 'partial', 'estimate'] as const;
/**
 * final: the marketplace's records of its sale (and, shipped on the marketplace's contract, of its cargo)
 * are in; partial: some are; estimate: none yet (they come after delivery), the figures are worked out.
 */
export type ProfitState = (typeof PROFIT_STATES)[number];

export const PROFIT_STATE_LABELS: Readonly<Record<ProfitState, string>> = { final: 'Kesin', partial: 'Kısmi', estimate: 'Tahmini' };

/** The report's order table lists the newest orders up to this many; its totals count every order of the period. */
export const PROFIT_ORDER_LIMIT = 5000;

export interface ProfitQuery {
  /** First day, Türkiye time (YYYY-MM-DD), by the orders' date */
  readonly from: IsoDate;
  /** Last day, inclusive */
  readonly to: IsoDate;
  readonly channel?: string | null;
  readonly credentialId?: string | null;
}

/**
 * Where an order's cargo figure comes from: the marketplace's records, the warehouse's own charge (its carrier
 * contract), an estimate from the seller's recent parcels, unknown (the seller's own contract, or no parcel to
 * go by), none (the order never left).
 */
export type CargoSource = 'rows' | 'warehouse' | 'estimate' | 'unknown' | 'none';

/** The parts of a profit, decimal strings in TRY; deductions and costs are negative. */
export interface ProfitFigures {
  /** what the goods sold for, KDV dahil (the marketplace's sales, else the order's own prices) */
  readonly ciro: string;
  /** the same, KDV hariç (each line's own rate) */
  readonly ciroNet: string;
  /** the marketplace's deductions, KDV hariç, by group */
  readonly commission: string;
  readonly cargo: string;
  readonly fees: string;
  /** returns and the seller's own discounts */
  readonly returns: string;
  /** penalties, ads, corrections, records not recognised */
  readonly other: string;
  /** all of the marketplace's deductions */
  readonly marketplace: string;
  /** the warehouse's fees (Pronto Depo), KDV hariç */
  readonly pronto: string;
  /** the goods' cost, less the units returned sellable */
  readonly productCost: string;
  /** net profit, KDV hariç */
  readonly profit: string;
  /** profit ÷ ciro (KDV hariç); null without a ciro */
  readonly margin: number | null;
  /** hak ediş: the ciro and the marketplace's deductions as charged, KDV dahil */
  readonly cash: string;
  /** e-commerce stopaj withheld (a tax paid in advance, not a cost) */
  readonly withholding: string;
}

export type ProfitEstimated = 'ciro' | 'commission' | 'cargo' | 'fees';

export interface ProfitOrderRow extends ProfitFigures {
  readonly orderId: string;
  readonly orderNumber: string;
  readonly channel: MarketplaceChannel;
  readonly credentialId: string | null;
  /** the marketplace account's name */
  readonly account: string | null;
  readonly orderedAt: IsoDateTime;
  readonly status: OrderStatus;
  readonly shippingModel: ShippingModel;
  readonly state: ProfitState;
  readonly units: number;
  /** the marketplace's records linked to it */
  readonly rows: number;
  /** figures worked out because their records are not in yet */
  readonly estimated: readonly ProfitEstimated[];
  readonly cargoSource: CargoSource;
  /** units sold whose cost is not entered (left out of the cost) */
  readonly unitsWithoutCost: number;
}

export interface ProfitProductRow extends ProfitFigures {
  /** null: lines that matched no product */
  readonly productId: string | null;
  readonly sku: string;
  readonly name: string;
  readonly orders: number;
  readonly units: number;
  readonly returnedUnits: number;
  /** the unit cost in force now; null: none */
  readonly unitCost: string | null;
  readonly unitsWithoutCost: number;
}

/** Marketplace records of no order, of one kind (and label), in the period. */
export interface ProfitAccountRow {
  readonly kind: FinanceKind;
  readonly label: string;
  readonly count: number;
  /** KDV hariç, signed */
  readonly net: string;
  /** as charged, signed */
  readonly cash: string;
  /** taken off the profit (ads, penalties, platform fees, corrections); otherwise only shown */
  readonly counted: boolean;
}

/** The warehouse's charges of no order in the period (storage, pallets, receiving). */
export interface ProfitPeriodFee {
  readonly type: ChargeType;
  readonly count: number;
  /** KDV hariç, as booked (positive) */
  readonly amount: string;
}

export interface ProfitSyncState {
  readonly credentialId: string;
  readonly channel: IntegratedChannel;
  readonly label: string;
  /** the marketplace gives finance records (Trendyol, Hepsiburada) and the account is on */
  readonly supported: boolean;
  /** the account takes orders (not switched off, its keys not refused) */
  readonly active: boolean;
  /** records are read up to then */
  readonly syncedUntil: IsoDateTime | null;
  /** the last time they were asked for */
  readonly checkedAt: IsoDateTime | null;
  readonly requestedAt: IsoDateTime | null;
  /** a read of the account is under way (asked for, or the night's): "okunuyor" until it ends */
  readonly running: boolean;
  /** why the last sync failed (Turkish; a missing finance role says what to do) */
  readonly error: string | null;
  readonly errorAt: IsoDateTime | null;
  /** "Şimdi güncelle" can be asked again then; null: now */
  readonly nextSyncAt: IsoDateTime | null;
}

export interface ProfitChannelRow {
  readonly channel: MarketplaceChannel;
  readonly orders: number;
  /** the marketplace's deductions come from its own records; otherwise they are unknown */
  readonly supported: boolean;
}

export interface ProfitTotals extends ProfitFigures {
  /** marketplace records of no order (in `marketplace` and `cash` when counted) */
  readonly accountNet: string;
  /** the warehouse's charges of no order (in `pronto`) */
  readonly periodFees: string;
}

export interface ProfitReport {
  readonly from: IsoDate;
  readonly to: IsoDate;
  readonly channel: string | null;
  readonly credentialId: string | null;
  readonly totals: ProfitTotals;
  readonly counts: {
    readonly orders: number;
    readonly final: number;
    readonly partial: number;
    readonly estimate: number;
    /**
     * orders of a marketplace that gives finance records whose records are not all in yet (estimate or
     * partial): they come after delivery, the figures meanwhile are worked out
     */
    readonly pending: number;
    /** orders of a marketplace whose finance records the warehouse cannot read: its deductions are unknown */
    readonly noFinance: number;
    /** orders in another currency than TRY: left out */
    readonly otherCurrency: number;
    /** shipped on the seller's own contract: the cargo is not known here */
    readonly cargoUnknown: number;
    readonly productsWithoutCost: number;
    readonly unitsWithoutCost: number;
  };
  /** newest first */
  readonly orders: readonly ProfitOrderRow[];
  /** most ciro first */
  readonly products: readonly ProfitProductRow[];
  readonly account: readonly ProfitAccountRow[];
  /** records of orders the warehouse never held (depo dışı siparişler): shown apart, not in the totals */
  readonly outside: { readonly orders: number; readonly net: string; readonly cash: string; readonly rows: readonly ProfitAccountRow[] };
  readonly periodFees: readonly ProfitPeriodFee[];
  /** false: a channel or account is looked at, and the charges of no order (storage) are not taken off */
  readonly periodFeesApplied: boolean;
  /** what the marketplaces paid out (or moved) in the period, positive: payouts and transfers, shown only */
  readonly payouts: string;
  readonly sync: readonly ProfitSyncState[];
  readonly channels: readonly ProfitChannelRow[];
}

/** A marketplace record of an order, as its line on the page shows it. */
export interface FinanceRowView {
  readonly id: string;
  readonly kind: FinanceKind;
  readonly label: string;
  readonly amount: string;
  readonly net: string;
  readonly vatIncluded: boolean;
  readonly currency: CurrencyCode;
  readonly occurredAt: IsoDateTime;
  readonly barcode: string | null;
  readonly quantity: number | null;
}

/** Everything behind one order's line: the marketplace's records and the warehouse's charges. */
export interface ProfitOrderDetail {
  readonly orderId: string;
  readonly orderNumber: string;
  readonly rows: readonly FinanceRowView[];
  readonly charges: readonly { readonly type: ChargeType; readonly description: string; readonly amount: string; readonly serviceDate: IsoDate }[];
}
